import { Center, FormLabel, SimpleGrid, Text } from '@chakra-ui/react';
import { wait } from '@v1v2/util';
import { Formik, Form, FormikConfig, FormikValues } from 'formik';
import {
  CheckboxContainer,
  CheckboxControl,
  CheckboxSingleControl,
  FormControl,
  InputControl,
} from 'formik-chakra-ui';
import { signIn } from 'next-auth/client';

import {
  validateSchema,
  initFormikValuesFromSchema,
} from '@inspire/data/helpers/schemas';
import { ProfProfileSchema } from '@inspire/data/schemas/user/profile/prof';
import { PedagogieProfileSchema } from '@inspire/data/schemas/user/profile/pedagogie';

import Select from '@inspire/ui/chakra/forms/Select';
import {
  titleOptions,
  pedagogieRoleOptions,
  departementOptionsInt,
  niveauOptions,
} from '@inspire/ui/select-options';
import {
  CheckboxControlOptions,
  FormStack,
} from '@inspire/ui/chakra/forms/Form';
import { ERROR_MESSAGE_EMAIL_EXISTS } from '@inspire/data/static/error-codes';

import SubmitButton from 'components/forms/SubmitButton';
import {
  GTM_ACTION_INSCRIPTION_PROF,
  GTM_ACTION_INSCRIPTION_REUSSIE,
  GTM_CATEGORY_INSCRIPTION,
  pushEvent,
} from 'lib/gtm';
import { didntWorkToast, emailAdreadyUsedToast } from 'lib/toasts';
import {
  MES_MODULES_PATH,
  VALIDATION_EMAIL_PATH,
  getAbsolutePath,
} from 'lib/paths';
import { handleErrors } from 'lib/errors';
import { useCreateProf, useLycees, useMyProfile, useUser } from 'lib/hooks';
import { Bacs2019 } from '@inspire/data/static/static';
import TagManager from 'lib/packages/react-gtm-module/TagManager';
import { useEffect, useState } from 'react';
import { getCookie } from 'lib/cookies';
import { groupBy } from 'lodash';

const SignupProfileSchema = ProfProfileSchema.pick(
  'title',
  'hadInspirePresentation',
  'pedagogie_departement',
  'pedagogie_lyceeId',
  'pedagogie_pays',
  'pedagogie_lyceeEtrangerName',
  'pedagogie_niveaux'
)
  .extend(
    PedagogieProfileSchema.pick('fonction', 'fonctionRole', 'pedagogie_filiere')
  )
  .requireFields()
  .extend({
    pedagogie_lyceeId: {
      optional: true,
      custom() {
        if (
          this.field('pedagogie_departement').value !== "A l'étranger" &&
          !this.isSet
        ) {
          return 'required';
        }
      },
    },
    pedagogie_pays: {
      optional: true,
      custom() {
        if (
          this.field('pedagogie_departement').value === "A l'étranger" &&
          !this.isSet
        ) {
          return 'required';
        }
      },
    },
    pedagogie_lyceeEtrangerName: {
      optional: true,
      custom() {
        if (
          this.field('pedagogie_departement').value === "A l'étranger" &&
          !this.isSet
        ) {
          return 'required';
        }
      },
    },
  });

let visitorId = '';

const SignupProfProfileForm = () => {
  const { createProfAsync } = useCreateProf();
  const { myProfile, ssoId } = useMyProfile();
  const { user } = useUser();
  const [departement, setDepartement] = useState();
  const { lycees, areLyceesLoading, refetchLycees } = useLycees(
    { departement },
    { enabled: !!departement }
  );

  useEffect(() => {
    departement && refetchLycees();
  }, [departement, refetchLycees]);

  useEffect(() => {
    visitorId = getCookie('visitorId');
  });

  const groupByCity = groupBy(lycees || [], 'ville');

  const lyceesOptions = Object.keys(groupByCity)
    .sort()
    .map((k) => ({
      label: k,
      options: groupByCity[k].map((l) => ({
        value: l._id,
        label: `${l.nom}`,
        city: l.ville,
        // @ts-ignore
        isPrioritaire: l.isPrioritaire,
      })),
    }));

  if (myProfile?.email !== undefined) {
    const formik: FormikConfig<FormikValues> = {
      initialValues: {
        ...initFormikValuesFromSchema(SignupProfileSchema),
        hadInspirePresentation: false,
        newsletter: myProfile?.newsletter ?? false,
      },
      validate: (values) => validateSchema(SignupProfileSchema, values),
      onSubmit: async (values) => {
        pushEvent(
          GTM_CATEGORY_INSCRIPTION,
          GTM_ACTION_INSCRIPTION_PROF,
          'Page email'
        );
        try {
          values.pedagogie_filiere.forEach((element) => {
            switch (element) {
              case 'bac_general':
                values.pedagogie_filiere_generale = true;
                break;
              case 'bac_techno':
                values.pedagogie_filiere_technologique = true;
                break;
              case 'bac_pro':
                values.pedagogie_filiere_professionnelle = true;
                break;
              case 'bac_techno_agro':
                values.pedagogie_filiere_techno_agro = true;
                break;
              case 'bac_pro_agro':
                values.pedagogie_filiere_pro_agro = true;
                break;
              default:
                break;
            }
          });
          delete values.pedagogie_filiere;
          delete values['pedagogie_filiere.$'];
          delete values['pedagogie_niveaux.$'];

          const res = await createProfAsync({
            profile: {
              ...values,
              update_newsletter: new Date(),
            },
          });

          // TODO: wrap createProf + login together
          if (res) {
            try {
              await signIn('credentials', {
                userId: res.createProf.id,
                ssoId: res.createProf.ssoId,
                callbackUrl: getAbsolutePath(
                  myProfile.isEmailVerified
                    ? MES_MODULES_PATH
                    : VALIDATION_EMAIL_PATH
                ),
              });
            } catch (err) {
              didntWorkToast();
              throw err;
            }

            // Inscription réussie
            TagManager.dataLayer({
              dataLayer: {
                event: 'sso_inscription',
                eventCategory: GTM_CATEGORY_INSCRIPTION,
                eventAction: GTM_ACTION_INSCRIPTION_REUSSIE,
                eventLabel: undefined,
                eventValue: undefined,
                accountId: user?.id,
                ssoId: ssoId,
                visitorId: visitorId,
                profil: 'pedagogie',
              },
            });

            // Keep the loading spinner during full page reload
            await wait(2000);
          }
        } catch ({ errors }) {
          console.error(errors);
          handleErrors(errors, [
            ERROR_MESSAGE_EMAIL_EXISTS,
            () => emailAdreadyUsedToast(),
          ]);
        }

        /* setProfSignupProfile(values);
      router.push(INSCRIPTION_PROF_COMPTE_PATH);
      pushEvent(
        GTM_CATEGORY_INSCRIPTION,
        GTM_ACTION_INSCRIPTION_PROF,
        'Page prénom'
      );*/
      },
    };

    return (
      <Formik {...formik}>
        {({ values, setFieldValue }) => (
          <Form>
            <FormStack>
              <FormControl
                isRequired
                name="title"
                label="Titre de civilité"
                labelProps={{ htmlFor: 'react-select-title-input' }}
              >
                <Select
                  instanceId="title"
                  openMenuOnFocus
                  options={titleOptions}
                  onChange={(selectedOption) => {
                    setFieldValue('title', selectedOption.value);
                  }}
                  value={titleOptions.find((o) => o.value === values.title)}
                  isSearchable={false}
                />
              </FormControl>

              <FormControl
                isRequired
                name="fonction"
                label="Rôle"
                labelProps={{ htmlFor: 'react-select-fonction-input' }}
              >
                <Select
                  instanceId="fonction"
                  openMenuOnFocus
                  options={pedagogieRoleOptions}
                  onChange={(selectedOption) => {
                    setFieldValue('fonction', selectedOption.value);
                  }}
                  value={titleOptions.find((o) => o.value === values.fonction)}
                  isSearchable={false}
                />
              </FormControl>

              <InputControl
                name="fonctionAutre"
                isRequired={values.fonction === 'autre'}
                label="Précisez votre rôle"
                isDisabled={values.fonction !== 'autre'}
                display={values.fonction === 'autre' ? 'block' : 'none'}
              />

              <FormControl
                name="pedagogie_departement"
                isRequired
                label="Dans quel département enseignez-vous ?"
                labelProps={{ htmlFor: 'react-select-departement-input' }}
              >
                <Select
                  instanceId="pedagogie_departement"
                  openMenuOnFocus
                  options={departementOptionsInt}
                  onChange={(selectedOption) => {
                    setFieldValue(
                      'pedagogie_departement',
                      selectedOption.value
                    );
                    setDepartement(selectedOption.value);
                  }}
                  isSearchable
                />
              </FormControl>

              {departement === "A l'étranger" ? (
                <>
                  <InputControl
                    name="pedagogie_pays"
                    isRequired
                    label="Dans quel pays enseignez-vous ?"
                  />
                  <InputControl
                    name="pedagogie_lyceeEtrangerName"
                    isRequired
                    label="Dans quel établissement enseignez-vous ?"
                  />
                </>
              ) : (
                <FormControl
                  name="pedagogie_lyceeId"
                  label="Dans quel établissement enseignez-vous ?"
                  labelProps={{ htmlFor: 'react-select-lyceeId-input' }}
                  isRequired
                  mb={2}
                  helperTextProps={{ color: 'gray.800' }}
                >
                  <Select
                    instanceId="lyceeId"
                    openMenuOnFocus
                    options={lyceesOptions}
                    isSearchable
                    isLoading={areLyceesLoading}
                    onChange={(selectedOption) => {
                      setFieldValue('pedagogie_lyceeId', selectedOption.value);
                    }}
                  />
                </FormControl>
              )}

              <FormLabel>
                <b>Voies </b>
                <Text as="span" color="red.500" fontWeight="bold">
                  *
                </Text>
              </FormLabel>
              <CheckboxContainer name="pedagogie_filiere">
                <SimpleGrid spacing={2} textAlign="left">
                  {Object.keys(Bacs2019).map((o, i) => (
                    <CheckboxControl
                      key={i}
                      value={o}
                      name={'pedagogie_filiere'}
                    >
                      {Bacs2019[o]}
                    </CheckboxControl>
                  ))}
                </SimpleGrid>
              </CheckboxContainer>

              <CheckboxContainer
                name="pedagogie_niveaux"
                label={
                  <>
                    <Text as="span" fontWeight="bold">
                      Niveaux
                    </Text>{' '}
                    <Text as="span" color="red.500" fontWeight="bold">
                      *
                    </Text>
                  </>
                }
                mt={10}
              >
                <CheckboxControlOptions
                  name="pedagogie_niveaux"
                  options={niveauOptions}
                />
              </CheckboxContainer>

              <CheckboxSingleControl
                name="hadInspirePresentation"
                label="J'ai suivi une présentation de la plateforme avec l'équipe INSPIRE"
              />
              <CheckboxSingleControl
                name="newsletter"
                label="Je souhaite recevoir des communications périodiques de l'équipe INSPIRE (conseils pour optimiser l’utilisation de la plateforme en classe, ressources utiles, invitations à des webinaires avec notre communauté de profs, etc…)"
              />
              <Center>
                <SubmitButton size="lg">Valider</SubmitButton>
              </Center>
            </FormStack>
          </Form>
        )}
      </Formik>
    );
  }
  return null;
};

export default SignupProfProfileForm;
